// client/src/components/MaterialsSection.js — partner materials on a
// mandate (migrations/079, portal review item 11), shared by the portal and
// the CRM mandate pages. Each slot is a card with a first-page thumbnail
// that opens the document; the intro email opens pre-filled in the
// viewer's own email app.
//
//   mode="portal"  partner view: filled slots and emails only
//   mode="crm"     RGA view: every slot marked Filled or Empty, upload or
//                  replace its file (MaterialUploadModal.js), edit the
//                  intro email
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { mailtoHref } from '../lib/materials'
import MaterialUploadModal from './MaterialUploadModal'
import { Modal } from './people/ui'

const SET_TITLE = { Investor: 'Investor Materials', Customer: 'Business Development Materials' }
const EMAIL_LABEL = { Investor: 'Investor Email', Customer: 'Customer Email' }
const SLOT_LABEL = {
  'Investor Deck': 'Investor Deck', 'Investor Teaser': 'Investor Teaser', 'Track Record': 'Track Record',
  'Customer Presentation': 'Customer Presentation', 'Customer Teaser': 'Customer Teaser',
}

const muted = { color: 'var(--text-muted)' }
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }
const cardBase = {
  display: 'flex', flexDirection: 'column', borderRadius: 10, overflow: 'hidden',
  border: '1px solid var(--border)', background: 'var(--bg-card, var(--surface))',
  transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
  textDecoration: 'none', color: 'inherit', minWidth: 0,
}
const hoverOn = e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 24px rgba(0,0,0,0.35)'; e.currentTarget.style.borderColor = 'var(--accent)' }
const hoverOff = e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = 'var(--border)' }

function Thumb({ doc }) {
  const box = { aspectRatio: '16 / 10', background: 'var(--hover-tint, rgba(255,255,255,0.04))', position: 'relative', overflow: 'hidden' }
  if (doc?.thumbnail_url) {
    return <div style={box}><img src={doc.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} /></div>
  }
  const ext = (doc?.file_name?.split('.').pop() || '').toUpperCase().slice(0, 4)
  return (
    <div style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: '0.08em', ...muted }}>{doc ? ext || 'FILE' : ''}</span>
    </div>
  )
}

// Also used by the CRM mandate page for its engagement letter cards.
export function SlotCard({ slot, children }) {
  const doc = slot.document
  const caption = (
    <div style={{ padding: '10px 12px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', ...muted }}>
        {SLOT_LABEL[slot.material_type] ?? slot.material_type}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={doc?.file_name}>
        {doc ? doc.file_name : <span style={muted}>Not loaded yet</span>}
      </div>
    </div>
  )
  if (!doc?.url) {
    return (
      <div style={{ ...cardBase, borderStyle: doc ? 'solid' : 'dashed' }}>
        <Thumb doc={doc} />{caption}{children}
      </div>
    )
  }
  return (
    <div style={cardBase} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
      <a href={doc.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
        <Thumb doc={doc} />{caption}
      </a>
      {children}
    </div>
  )
}

function EmailCard({ audience, email, onEdit }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText([email.subject && `Subject: ${email.subject}`, email.body].filter(Boolean).join('\n\n'))
      setCopied(true); setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked */ }
  }
  return (
    <div style={{ ...cardBase, borderStyle: email ? 'solid' : 'dashed' }}>
      <div style={{ aspectRatio: '16 / 10', padding: 14, overflow: 'hidden', background: 'var(--hover-tint, rgba(255,255,255,0.04))',
        fontSize: 11, lineHeight: 1.5, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap',
        maskImage: 'linear-gradient(to bottom, black 70%, transparent)', WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent)' }}>
        {email ? <>{email.subject && <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>{email.subject}</div>}{email.body}</> : ''}
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', ...muted }}>{EMAIL_LABEL[audience]}</div>
        {email ? (
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <a className="btn btn-primary btn-sm" href={mailtoHref(email)}>Open in email</a>
            <button type="button" className="btn btn-secondary btn-sm" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
            {onEdit && <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>Edit</button>}
          </div>
        ) : (
          <div style={{ fontSize: 13, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={muted}>Not written yet</span>
            {onEdit && <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>Write</button>}
          </div>
        )}
      </div>
    </div>
  )
}

function EmailModal({ mandateId, audience, email, onClose }) {
  const qc = useQueryClient()
  const [subject, setSubject] = useState(email?.subject ?? '')
  const [body, setBody] = useState(email?.body ?? '')
  const save = useMutation({
    mutationFn: () => api.put(`/api/mandates/${mandateId}/intro-emails/${audience}`, { subject, body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['materials', mandateId] }); onClose() },
  })
  return (
    <Modal title={EMAIL_LABEL[audience]} onClose={onClose} width={640}>
      <form onSubmit={e => { e.preventDefault(); save.mutate() }} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input className="input" placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} />
        <textarea className="input" rows={14} placeholder="Email text the partner sends (leave empty to remove)"
          value={body} onChange={e => setBody(e.target.value)} style={{ fontFamily: 'inherit', resize: 'vertical' }} />
        {save.isError && <div style={{ fontSize: 12, color: 'var(--danger-text)' }}>{save.error.response?.data?.error || save.error.message}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  )
}

// CRM: whether the slot is filled, and the button that uploads into it.
function SlotStatus({ slot, onUpload }) {
  const filled = !!slot.document
  return (
    <div style={{ padding: '0 12px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <span className={`badge${filled ? ' badge-green' : ''}`}>{filled ? 'Filled' : 'Empty'}</span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onUpload}>{filled ? 'Replace' : 'Upload'}</button>
    </div>
  )
}

export default function MaterialsSection({ mandateId, mode = 'portal' }) {
  const crm = mode === 'crm'
  const [editing, setEditing] = useState(null)
  const [uploading, setUploading] = useState(null)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['materials', mandateId],
    queryFn: () => api.get(crm ? `/api/mandates/${mandateId}/materials` : `/api/portal/mandates/${mandateId}/materials`).then(r => r.data.data),
  })
  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><div className="spinner" /></div>
  if (isError) return <div style={{ fontSize: 13, ...muted }}>Materials could not be loaded.</div>

  const sets = (data?.sets ?? []).map(s => crm ? s : { ...s, slots: s.slots.filter(x => x.document) })
  const anything = sets.some(s => s.slots.length || s.email)
  if (!sets.length) return <div style={{ fontSize: 13, ...muted }}>No materials are set up for this mandate's work type.</div>
  if (!crm && !anything) return <div style={{ fontSize: 13, ...muted }}>Materials for this mandate will appear here.</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {sets.map(s => (
        <div key={s.audience}>
          {sets.length > 1 && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: 'var(--text-secondary)' }}>{SET_TITLE[s.audience]}</div>}
          <div style={grid}>
            {s.slots.map(slot => (
              <SlotCard key={slot.material_type} slot={slot}>
                {crm && <SlotStatus slot={slot} onUpload={() => setUploading(slot.material_type)} />}
              </SlotCard>
            ))}
            {(crm || s.email) && (
              <EmailCard audience={s.audience} email={s.email} onEdit={crm ? () => setEditing(s) : null} />
            )}
          </div>
        </div>
      ))}
      {uploading && <MaterialUploadModal mandateId={mandateId} materialType={uploading} onClose={() => setUploading(null)} />}
      {editing && <EmailModal mandateId={mandateId} audience={editing.audience} email={editing.email} onClose={() => setEditing(null)} />}
    </div>
  )
}
